import { formatMoney } from "@sylviq/ui-core";
import { Button } from "@sylviq/ui-react-web/components/ui/button";
import type { BillStatusCount, WorklistAgingBucket } from "@/lib/api";

// Compact attention strip for the invoice/bill worklists: Outstanding /
// Overdue / Open plus an aging bar and legend. The bucket labels come from
// the server and line up with the AgedTables surfaces ("Current", "1-30
// days", …). A failed read renders as an honest error with retry — never as
// a synthetic zero.

export interface WorklistSummaryProps {
  /** Accessible name, e.g. "Invoice summary". */
  title: string;
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  totalCount: number;
  openCount: number;
  outstandingCents: number;
  overdueCents: number;
  aging: WorklistAgingBucket[];
  /** ISO 4217 code for all amounts; defaults to USD. */
  currency?: string;
  /** Optional per-status counts (bills). */
  byStatus?: BillStatusCount[];
  /** Shown instead of the strip when the read succeeds with no documents. */
  emptyMessage?: string;
  onSelectAging?: (label: string) => void;
  onSelectStatus?: (status: string) => void;
  /** Outstanding amount selects a status, such as awaiting payment. */
  onSelectOutstanding?: () => void;
  /** Overdue amount selects the overdue status. */
  onSelectOverdue?: () => void;
  outstandingSelected?: boolean;
  overdueSelected?: boolean;
  /** Invoice summary hides the open count; bills keep it. */
  showOpen?: boolean;
  /** Invoice summary hides the aging bar; bills keep it. */
  showAging?: boolean;
  activeAging?: string | null;
  activeStatus?: string | null;
  testId?: string;
}

function normalizeAgingLabel(label: string): string {
  return label
    .trim()
    .toLowerCase()
    .replace(/\s*days?$/, "");
}

function agingColor(label: string): string {
  switch (normalizeAgingLabel(label)) {
    case "current":
      return "var(--success)";
    case "1-30":
      return "var(--warning)";
    case "31-60":
      return "var(--action)";
    case "61-90":
      return "color-mix(in srgb, var(--danger) 60%, var(--surface))";
    default:
      return "var(--danger)";
  }
}

function Stat({
  label,
  value,
  detail,
  tone,
  onClick,
  pressed,
}: {
  label: string;
  value: string;
  detail?: string;
  tone?: "danger";
  onClick?: () => void;
  pressed?: boolean;
}) {
  const body = (
    <>
      <span className="text-[0.68rem] font-bold tracking-[0.08em] text-[var(--muted-foreground)] uppercase">
        {label}
      </span>
      <span
        className={`text-xl font-semibold tabular-nums ${
          tone === "danger" ? "text-[var(--danger)]" : ""
        }`}
      >
        {value}
      </span>
      {detail ? (
        <span className="text-xs text-[var(--muted-foreground)]">{detail}</span>
      ) : null}
    </>
  );
  if (!onClick) return <div className="grid gap-0.5 text-start">{body}</div>;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={pressed}
      className={`grid gap-0.5 rounded-xl px-2 py-1 text-start hover:bg-[var(--muted)] ${
        pressed ? "bg-[var(--muted)]" : ""
      }`}
    >
      {body}
    </button>
  );
}

export function WorklistSummary({
  title,
  loading,
  error,
  onRetry,
  totalCount,
  openCount,
  outstandingCents,
  overdueCents,
  aging,
  currency = "USD",
  byStatus,
  emptyMessage,
  onSelectAging,
  onSelectStatus,
  onSelectOutstanding,
  onSelectOverdue,
  outstandingSelected = false,
  overdueSelected = false,
  showOpen = true,
  showAging = true,
  activeAging = null,
  activeStatus = null,
  testId,
}: WorklistSummaryProps) {
  const money = (cents: number) => formatMoney({ cents, currency });

  if (!loading && !error && totalCount === 0 && !emptyMessage) return null;

  return (
    <section
      aria-label={title}
      aria-busy={loading}
      data-testid={testId}
      className="surface grid gap-4 rounded-2xl p-5"
    >
      {loading ? (
        <p className="text-sm text-[var(--muted-foreground)]">
          Loading summary…
        </p>
      ) : error ? (
        <div role="alert" className="grid gap-3 text-sm text-[var(--danger)]">
          <span>{error}</span>
          <span>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={onRetry}
            >
              Retry
            </Button>
          </span>
        </div>
      ) : totalCount === 0 ? (
        <p className="text-sm text-[var(--muted-foreground)]">{emptyMessage}</p>
      ) : (
        <>
          <div className={`grid gap-4 ${showOpen ? "sm:grid-cols-3" : "sm:grid-cols-2"}`}>
            <Stat
              label="Outstanding"
              value={money(outstandingCents)}
              onClick={onSelectOutstanding}
              pressed={outstandingSelected}
            />
            <Stat
              label="Overdue"
              value={money(overdueCents)}
              tone={overdueCents > 0 ? "danger" : undefined}
              onClick={onSelectOverdue}
              pressed={overdueSelected}
            />
            {showOpen ? (
              <Stat
                label="Open"
                value={String(openCount)}
                detail={`of ${totalCount} total`}
              />
            ) : null}
          </div>

          {showAging && aging.length > 0 && aging.some((b) => b.total_cents > 0) ? (
            <div className="grid gap-2">
              <div
                className="flex h-2.5 w-full overflow-hidden rounded-full bg-[var(--muted)]"
                role="img"
                aria-label={`${title}: aging by bucket`}
              >
                {aging
                  .filter((bucket) => bucket.total_cents > 0)
                  .map((bucket) => (
                    <span
                      key={bucket.label}
                      title={`${bucket.label}: ${money(bucket.total_cents)}`}
                      style={{
                        flexBasis: 0,
                        flexGrow: bucket.total_cents,
                        backgroundColor: agingColor(bucket.label),
                      }}
                    />
                  ))}
              </div>
              <ul className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-[var(--muted-foreground)]">
                {aging.map((bucket) => {
                  const content = (
                    <>
                      <span
                        aria-hidden="true"
                        className="inline-block size-2 rounded-full align-middle"
                        style={{ backgroundColor: agingColor(bucket.label) }}
                      />
                      <span className="align-middle">
                        {bucket.label} · {bucket.count} ·{" "}
                        <span className="tabular-nums">
                          {money(bucket.total_cents)}
                        </span>
                      </span>
                    </>
                  );
                  const active = activeAging === bucket.label;
                  return (
                    <li key={bucket.label}>
                      {onSelectAging ? (
                        <button
                          type="button"
                          aria-pressed={active}
                          onClick={() => onSelectAging(bucket.label)}
                          className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 hover:bg-[var(--muted)] ${
                            active
                              ? "bg-[var(--muted)] font-semibold text-[var(--foreground)]"
                              : ""
                          }`}
                        >
                          {content}
                        </button>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 px-2 py-0.5">
                          {content}
                        </span>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          ) : showAging ? (
            <p className="text-sm text-[var(--muted-foreground)]">
              No outstanding balances to age.
            </p>
          ) : null}

          {byStatus && byStatus.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {byStatus.map((row) => {
                const active = activeStatus === row.status;
                const label = `${row.status} (${row.count})`;
                return onSelectStatus ? (
                  <button
                    key={row.status}
                    type="button"
                    aria-pressed={active}
                    onClick={() => onSelectStatus(row.status)}
                    title={`Filter to ${row.status} — ${money(row.total_cents)}`}
                    className={`rounded-full border px-3 py-1 text-xs capitalize ${
                      active
                        ? "border-[var(--action)] bg-[var(--action)] text-[var(--action-foreground)]"
                        : "border-[var(--border)] text-[var(--muted-foreground)] hover:bg-[var(--muted)]"
                    }`}
                  >
                    {label}
                  </button>
                ) : (
                  <span
                    key={row.status}
                    className="rounded-full border border-[var(--border)] px-3 py-1 text-xs capitalize text-[var(--muted-foreground)]"
                  >
                    {label}
                  </span>
                );
              })}
            </div>
          ) : null}
        </>
      )}
    </section>
  );
}
